import {
  WalletKeys,
  WalletsTableSortKeys,
} from 'api/types/httpsTypes/contracts';
import {
  FilterState,
  useWalletsSearchFilters,
  WalletPeriods,
} from './usePageParams';
import { ChainsIcon } from 'modules/shared-components/exchange/exchange-icon';
import { chainAsset, chainName, Chains } from 'api/types/httpsTypes/d-wallets';
import { useEffect, useState } from 'react';
import { useDebouncedState } from 'utils/useDebounce';

import { FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { BalanceRangeFilter, LabelsFilter, PnlFilter } from './ResearchFilters';
import { isNotNil } from 'modules/utils/isNotNil';
import {
  allSuffixes,
  formatNumberWithSuffix,
  safeBig,
} from 'utils/FormatNumber';
import { twMerge } from 'tailwind-merge';
import { isValidAddress } from 'helpers/api/web3Utils';
import {
  SortDropdown,
  SortDropdownProps,
} from '../../../components/dropdown/sort-dropdown';
import {
  ActivteFiltersBar,
  FilterTag,
  SelectFiltersBar,
} from '../../../components/filters';
import QuickFilters from '../../../components/filters/quickFilters';
import TextInput from '../../../components/inputs/text-input/text-input';
import { useGlobalChain } from 'modules/routing/chainProvider';
import Button from 'modules/shared-components/button/button';
import { useChainInfo } from 'api/hooks/useChainInfo';

export const SORT_OPTIONS = (isChainAsset: boolean) => {
  return [
    {
      label: 'Total PnL',
      value: `-${WalletsTableSortKeys[isChainAsset ? 'PnlQuote' : 'PnlUsd']}`,
    },

    {
      label: 'Number of Wins',
      value: `-${
        WalletsTableSortKeys[isChainAsset ? 'NOfWinsQuote' : 'NOfWinsUsd']
      }`,
    },

    {
      label: 'Balance',
      value: `-${WalletsTableSortKeys.Balance}`,
    },

    {
      label: 'Tokens',
      value: `-${WalletsTableSortKeys.UniqueTokens}`,
    },

    {
      label: 'Holding Time',
      value: `-${WalletsTableSortKeys.HoldingTime}`,
    },
    {
      label: 'Last Swap',
      value: `-${WalletsTableSortKeys.LastSwap}`,
    },
  ];
};
export const CHAINS_OPTIONS = [
  {
    value: Chains.Ethereum,
    label: chainName(Chains.Ethereum),
    icon: <ChainsIcon imgName={Chains.Ethereum} />,
  },
  {
    value: Chains.Base,
    label: chainName(Chains.Base),
    icon: <ChainsIcon imgName={Chains.Base} />,
  },
  {
    value: Chains.BinanceChain,
    label: chainName(Chains.BinanceChain),
    icon: <ChainsIcon imgName={Chains.BinanceChain} />,
  },
];

function getNextFilters(filters: FilterState, networkPrice: string | number) {
  const goingToUsd = filters.isChainAsset; // true if switching chain → USD

  // Adjust sort
  let newSort = filters.sort;
  if (goingToUsd) {
    if (filters.sort === `-${WalletsTableSortKeys.PnlQuote}`) {
      newSort = `-${WalletsTableSortKeys.PnlUsd}`;
    } else if (filters.sort === `${WalletsTableSortKeys.PnlQuote}`) {
      newSort = `${WalletsTableSortKeys.PnlUsd}`;
    } else if (filters.sort === `${WalletsTableSortKeys.NOfWinsQuote}`) {
      newSort = `${WalletsTableSortKeys.NOfWinsUsd}`;
    }
  } else {
    if (filters.sort === `-${WalletsTableSortKeys.PnlUsd}`) {
      newSort = `-${WalletsTableSortKeys.PnlQuote}`;
    } else if (filters.sort === `${WalletsTableSortKeys.PnlUsd}`) {
      newSort = `${WalletsTableSortKeys.PnlQuote}`;
    } else if (filters.sort === `${WalletsTableSortKeys.NOfWinsUsd}`) {
      newSort = `${WalletsTableSortKeys.NOfWinsQuote}`;
    }
  }

  // Adjust min/max PnL
  let newMinPnl = safeBig(filters.minPnl);
  let newMaxPnl = safeBig(filters.maxPnl);

  if (safeBig(networkPrice).gt(0)) {
    if (goingToUsd) {
      newMinPnl = newMinPnl.mul(networkPrice);
      newMaxPnl = newMaxPnl.mul(networkPrice);
    } else {
      newMinPnl = newMinPnl.div(networkPrice);
      newMaxPnl = newMaxPnl.div(networkPrice);
    }
  }

  return {
    ...filters,
    isChainAsset: !filters.isChainAsset,
    sort: newSort,
    ...(safeBig(filters.minPnl).gt(0) ? { minPnl: newMinPnl.toNumber() } : {}),
    ...(safeBig(filters.maxPnl).gt(0) ? { maxPnl: newMaxPnl.toNumber() } : {}),
  };
}

export const WalletsFiltersBar = () => {
  const { filtersState: filters, setFilters } = useWalletsSearchFilters();
  const [walletAddress, debouncedWalletAddress, setWalletAddress] =
    useDebouncedState('', 250);
  const [selectedChain] = useGlobalChain();
  const chain = selectedChain;
  const [addressError, setAddressError] = useState(false);

  useEffect(() => {
    if (debouncedWalletAddress) {
      setAddressError(!isValidAddress(debouncedWalletAddress, selectedChain));
      if (isValidAddress(debouncedWalletAddress, selectedChain)) {
        updateFilters({
          addressFilter: debouncedWalletAddress,
        });
      }
    }
  }, [debouncedWalletAddress]);

  const handleWalletAddressInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();

    const inputValue = e.target.value;
    setWalletAddress(inputValue);

    if (!inputValue) {
      setAddressError(false);
      updateFilters({
        addressFilter: undefined,
        tradingStyle: WalletKeys.TopCopy,
      });
    } else {
      updateFilters({
        tradingStyle: undefined,
      });
    }
  };

  const updateFilters = (update: Partial<FilterState>) => {
    setFilters({ ...filters, ...update });
  };

  const quickFilterKol = (isChainAsset: boolean) => {
    return setFilters({
      period: WalletPeriods.Thirty,
      sort: `-${WalletsTableSortKeys[isChainAsset ? 'PnlQuote' : 'PnlUsd']}`,
      tradingStyle: WalletKeys.Kol,
    } as FilterState);
  };

  const quickFilterTopSniper = (isChainAsset: boolean) => {
    return setFilters({
      period: WalletPeriods.Thirty,
      sort: `-${WalletsTableSortKeys[isChainAsset ? 'PnlQuote' : 'PnlUsd']}`,
      tradingStyle: WalletKeys.TopSniper,
    } as FilterState);
  };

  const quickFilterTopCopy = (isChainAsset: boolean) => {
    return setFilters({
      period: WalletPeriods.Thirty,
      sort: `-${WalletsTableSortKeys[isChainAsset ? 'PnlQuote' : 'PnlUsd']}`,
      tradingStyle: WalletKeys.TopCopy,
    } as FilterState);
  };

  const quickFilterTopTrader = (isChainAsset: boolean) => {
    return setFilters({
      period: WalletPeriods.Thirty,
      sort: `-${WalletsTableSortKeys[isChainAsset ? 'PnlQuote' : 'PnlUsd']}`,
      tradingStyle: WalletKeys.Trader,
    } as FilterState);
  };

  const quickFilterEarlyApe = (isChainAsset: boolean) => {
    return setFilters({
      period: WalletPeriods.Thirty,
      sort: `-${WalletsTableSortKeys[isChainAsset ? 'PnlQuote' : 'PnlUsd']}`,
      tradingStyle: WalletKeys.EarlyApe,
    } as FilterState);
  };

  useEffect(() => {
    if (
      selectedChain !== Chains.Solana &&
      selectedChain !== Chains.BinanceChain
    ) {
      if (filters.tradingStyle === WalletKeys.Kol) {
        quickFilterTopCopy(!!filters.isChainAsset);
      }
    }
  }, [selectedChain]);

  const quickFilterOptions = (isChainAsset: boolean) => {
    const options = [
      {
        label: 'Top Apes',
        onClick: () => quickFilterEarlyApe(isChainAsset),
        condition: filters.tradingStyle === WalletKeys.EarlyApe,
        extraClasses: 'flex',
      },
      {
        label: 'Top Traders',
        onClick: () => quickFilterTopTrader(isChainAsset),
        condition: filters.tradingStyle === WalletKeys.Trader,
        extraClasses: 'flex',
      },
      {
        label: 'Top Snipers',
        onClick: () => quickFilterTopSniper(isChainAsset),
        condition: filters.tradingStyle === WalletKeys.TopSniper,
        extraClasses: 'hidden lg:flex',
      },
      {
        label: 'Best To Copy',
        onClick: () => quickFilterTopCopy(isChainAsset),
        condition: filters.tradingStyle === WalletKeys.TopCopy,
        extraClasses: 'flex',
      },
    ];

    if (chain === Chains.Solana || chain === Chains.BinanceChain) {
      options.unshift({
        label: 'KOL',
        onClick: () => quickFilterKol(isChainAsset),
        condition: filters.tradingStyle === WalletKeys.Kol,
        extraClasses: 'flex',
      });
    }

    return options;
  };

  const nOfFiltersActive = [
    filters.tradingStyle,
    filters.addressFilter,
    filters.minPnl,
    filters.maxPnl,
    filters.minBalance,
    filters.maxBalance,
  ].filter((value) => value !== undefined && value && value !== '').length;

  const chainInfo = useChainInfo(chain);
  const networkPrice = chainInfo?.price ?? 0;

  return (
    <div className="xxs:mx-1 space-y-1 lg:mx-0">
      <SelectFiltersBar
        className="xxs:justify-start space-x-2"
        icon={
          <div
            className={twMerge(
              'xxs:p-1 flex xxs:space-x-1 items-center',
              nOfFiltersActive
                ? 'xxs:hover:text-dex-white'
                : ' xxs:hover:text-dex-white xxs:text-dex-white-secondary xxs:px-2 '
            )}
          >
            <FunnelIcon aria-hidden="true" width={16} height={16} />
            {nOfFiltersActive ? <div>{nOfFiltersActive}</div> : ''}
          </div>
        }
        isCondensed={true}
        sort={
          <div className="xxs:flex xxs:space-x-2">
            <div className="">
              <TextInput
                name="addressFilter"
                error={addressError ? 'Address not valid' : ''}
                value={walletAddress}
                prefix={
                  <MagnifyingGlassIcon className="w-4 xxs:text-dex-white-secondary" />
                }
                onChange={handleWalletAddressInputChange}
                placeholder="Wallet Address"
              />
            </div>
            <div className="xxs:pr-1 xxs:hidden lg:block ">
              <Button
                key="deskt"
                size="small"
                variant="dexNeutral"
                className="xxs:p-2"
                type="button"
                onClick={() => {
                  const next = getNextFilters(filters, networkPrice);
                  updateFilters(next);
                }}
              >
                <div className="normal-case">
                  Switch to {!filters.isChainAsset ? chainAsset(chain) : 'USD'}
                </div>
              </Button>
            </div>

            <div className="lg:hidden">
              <Sort
                isChainAsset={!!filters.isChainAsset}
                value={filters.sort}
                onChange={(value) => {
                  updateFilters({
                    sort: value,
                  });
                }}
              />
            </div>
          </div>
        }
      >
        <div className="xxs:space-y-2">
          <div className="xxs:hidden lg:block xxs:text-dex-white-secondary">
            Use advanced filters to find the best wallets to copy-trade.
          </div>
          <div className="xxs:pr-1 lg:hidden ">
            <Button
              key="mob"
              size="small"
              variant="dexNeutral"
              className="xxs:p-2 w-full"
              type="button"
              onClick={() => {
                const next = getNextFilters(filters, networkPrice);
                updateFilters(next);
              }}
            >
              <div className="normal-case">
                Switch to {!filters.isChainAsset ? chainAsset(chain) : 'USD'}
              </div>
            </Button>
          </div>

          <TextInput
            name="addressFilter"
            error={addressError ? 'Address not valid' : ''}
            value={walletAddress}
            prefix={
              <MagnifyingGlassIcon className="w-4 xxs:text-dex-white-secondary" />
            }
            onChange={handleWalletAddressInputChange}
            placeholder="Wallet Address"
          />

          <LabelsFilter />
          <BalanceRangeFilter />
          <PnlFilter chain={chain} />
        </div>
      </SelectFiltersBar>
      <QuickFilters
        filterOptions={quickFilterOptions(!!filters.isChainAsset)}
      />
      <ActivteFiltersBar>
        {filters.addressFilter && (
          <FilterTag
            onRemove={() => {
              setWalletAddress('');
              return updateFilters({
                addressFilter: undefined,
                tradingStyle: WalletKeys.TopCopy,
              });
            }}
            label={`Wallet: ${filters.addressFilter}` || ''}
          />
        )}
        {filters.tradingStyle && (
          <FilterTag
            onRemove={() => {
              if (filters.addressFilter) {
                updateFilters({ ...filters, tradingStyle: undefined });
              }
            }}
            label={`${
              filters.tradingStyle === WalletKeys.TopSniper
                ? '🎯 Top Sniper'
                : filters.tradingStyle === WalletKeys.EarlyApe
                ? '🐒 Early Ape'
                : filters.tradingStyle === WalletKeys.Trader
                ? '📈 Top Trader'
                : filters.tradingStyle === WalletKeys.Kol
                ? 'KOL'
                : '📄 Best To Copy'
            }`}
          />
        )}

        {isNotNil(filters.minPnl) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                minPnl: undefined,
              })
            }
            label={`PnL > ${
              !filters.isChainAsset ? '$' : ''
            }${formatNumberWithSuffix(filters.minPnl, {
              suffixes: allSuffixes,
              precision: 0,
            })}${filters.isChainAsset ? chainAsset(chain) : ''}`}
          />
        )}
        {isNotNil(filters.maxPnl) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                maxPnl: undefined,
              })
            }
            label={`PnL < ${
              !filters.isChainAsset ? '$' : ''
            }${formatNumberWithSuffix(filters.maxPnl, {
              suffixes: allSuffixes,
              precision: 0,
            })}${filters.isChainAsset ? chainAsset(chain) : ''}`}
          />
        )}

        {isNotNil(filters.minBalance) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                minBalance: undefined,
              })
            }
            label={`Balance > ${formatNumberWithSuffix(filters.minBalance, {
              suffixes: allSuffixes,
              precision: 0,
            })}${chainAsset(chain)}`}
          />
        )}
        {isNotNil(filters.maxBalance) && (
          <FilterTag
            onRemove={() =>
              updateFilters({
                maxBalance: undefined,
              })
            }
            label={`Balance < ${formatNumberWithSuffix(filters.maxBalance, {
              suffixes: allSuffixes,
              precision: 0,
            })}${chainAsset(chain)}`}
          />
        )}
      </ActivteFiltersBar>
    </div>
  );
};

type SortProps = Omit<SortDropdownProps<string>, 'options'> & {
  isChainAsset: boolean;
};

export function Sort({ isChainAsset, ...props }: SortProps) {
  return (
    <SortDropdown
      size="xs"
      className="xxs:py-1.5 xxs:px-2 h-auto"
      {...props}
      options={SORT_OPTIONS(isChainAsset)}
    />
  );
}
